import { BaseApiService, ApiResponse } from './baseApi';
import { User, UserRole, UserBranch, UserMenuPermission, LoginRequest, LoginResponse } from '../types/user';
import { API_CONFIG } from '../config/api';

class AuthApiService extends BaseApiService {
  private static instance: AuthApiService;

  private constructor() {
    super();
  }

  public static getInstance(): AuthApiService {
    if (!AuthApiService.instance) {
      AuthApiService.instance = new AuthApiService();
    }
    return AuthApiService.instance;
  }

  /**
   * Authenticate user with username and password
   */
  async authenticateUser(username: string, password: string): Promise<ApiResponse<LoginResponse>> {
    try {
      const requestBody = {
        username,
        password
      };

      // Login endpoint should NOT include Authorization header
      // Create a custom request without the token
      const url = `${API_CONFIG.BASE_URL}/auth/users/authenticate`;
      const loginResponse = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!loginResponse.ok) {
        let errorMessage = `HTTP error! status: ${loginResponse.status}`;
        try {
          const errorData = await loginResponse.json();
          if (errorData.message) {
            errorMessage += ` - ${errorData.message}`;
          }
        } catch (parseError) {
          // If we can't parse the error response, use the default message
        }
        const error = new Error(errorMessage) as any;
        error.status = loginResponse.status;
        throw error;
      }

      const response = await loginResponse.json() as ApiResponse<LoginResponse>;
      // Store token in localStorage if present
      if (response.success && response.data) {
        const loginData = response.data as LoginResponse;
        
        // Store JWT token from the new response structure
        if (loginData.token) {
          localStorage.setItem('authToken', loginData.token);
        }
        
        // Store user info from userInfo object
        if (loginData.userInfo) {
          if (loginData.userInfo.user) {
            authApiService.storeUserInfo(loginData.userInfo.user);
          }
          if (loginData.userInfo.userBranches) {
            authApiService.storeUserBranches(loginData.userInfo.userBranches);
          }
          if (loginData.userInfo.userMenuPermissions) {
            authApiService.storeUserMenuPermissions(loginData.userInfo.userMenuPermissions);
          }
        }
      }

      return response;
    } catch (error) {
      // console.error('Authentication failed:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Authentication failed. Please check your credentials.');
    }
  }

  /**
   * Logout user and clear stored tokens
   */
  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userBranches');
    localStorage.removeItem('userMenuPermissions');
  }

  /**
   * Get stored authentication token
   */
  getAuthToken(): string | null {
    return localStorage.getItem('authToken');
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    const userInfo = this.getUserInfo();
    // User is authenticated if they have a valid JWT token
    return !!(token && userInfo);
  }

  /**
   * Get stored user information
   */
  getUserInfo(): User | null {
    const userInfo = localStorage.getItem('userInfo');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * Store user information
   */
  storeUserInfo(user: User): void {
    localStorage.setItem('userInfo', JSON.stringify(user));
  }

  /**
   * Store user branches
   */
  storeUserBranches(userBranches: UserBranch[]): void {
    localStorage.setItem('userBranches', JSON.stringify(userBranches));
  }

  /**
   * Store user menu permissions
   */
  storeUserMenuPermissions(userMenuPermissions: UserMenuPermission[]): void {
    localStorage.setItem('userMenuPermissions', JSON.stringify(userMenuPermissions));
  }

  /**
   * Get stored user branches
   */
  getUserBranches(): UserBranch[] {
    const userBranches = localStorage.getItem('userBranches');
    return userBranches ? JSON.parse(userBranches) : [];
  }

  /**
   * Get stored user menu permissions
   */
  getUserMenuPermissions(): UserMenuPermission[] {
    const userMenuPermissions = localStorage.getItem('userMenuPermissions');
    return userMenuPermissions ? JSON.parse(userMenuPermissions) : [];
  }

  /**
   * Validate current token
   * Note: JWT tokens expire after 24 hours. When expired, users must log in again.
   * Token validation happens automatically on each API request via 401/403 responses.
   */
  async validateToken(): Promise<boolean> {
    try {
      const token = this.getAuthToken();
      const userInfo = this.getUserInfo();
      
      if (!token || !userInfo) {
        return false;
      }

      // If we have both token and userInfo, consider it valid
      // The token will be validated on each API request automatically
      // When the token expires (after 24 hours), API calls will return 401/403
      // and the user will be redirected to login via handleSessionExpiration in baseApi.ts
      
      return true;
    } catch (error) {
      // console.error('Token validation failed:', error);
      // If validation fails, don't immediately logout
      // The token will be validated on actual API calls
      // Only logout if we get a 401/403 on actual API requests
      return true; // Return true to allow the user to stay logged in
    }
  }
}

export const authApiService = AuthApiService.getInstance();
